import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';

interface BulkVars {
  groupId: string;
  /** Users to add to the group (already filtered to non-members by the caller). */
  add: string[];
  /** Users to remove from the group (already filtered to members). */
  remove: string[];
}

/**
 * Bulk group-membership editing from the Users table: add/remove many users
 * to/from one group in a single action. The API has only single-member
 * endpoints, so we fan out the writes and invalidate ONCE at the end (vs the
 * per-call invalidation the single-member hooks do).
 *
 * Membership changes every touched user's effective scopes, which the users
 * list, the group detail/list, and the caller's own chat gate (`me`) all read.
 */
export const useUsersBulkMembershipLogic = (tenantId: string | undefined) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<void, Error, BulkVars>({
    mutationFn: async ({ groupId, add, remove }) => {
      const base = `/api/groups/${groupId}/members`;
      await Promise.all([
        ...add.map((userId) => apiRequest<void>(base, { method: 'POST', body: { userId } })),
        ...remove.map((userId) => apiRequest<void>(`${base}/${userId}`, { method: 'DELETE' })),
      ]);
    },
    onSuccess: (_v, { groupId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users(tenantId ?? '') });
      void queryClient.invalidateQueries({ queryKey: queryKeys.groups(tenantId ?? '') });
      void queryClient.invalidateQueries({ queryKey: queryKeys.group(tenantId ?? '', groupId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me() });
    },
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    apply: async (vars: BulkVars) => mutation.mutateAsync(vars),
  };
};
